import { Link } from "react-router-dom";
import type { Entry } from "@/types/content";
import { formatDate, formatHeat, getCategoryName } from "@/utils/content";

interface EntryCardProps {
  entry: Entry;
  layout?: "card" | "row";
  rank?: number;
}

export default function EntryCard({ entry, layout = "card", rank }: EntryCardProps) {
  if (layout === "row") {
    return (
      <article className="group flex gap-3 border-b border-background-200/70 py-4 last:border-b-0 md:gap-5 md:py-5">
        {typeof rank === "number" && (
          <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-background-100 text-xs font-semibold text-primary-600">
            {String(rank).padStart(2, "0")}
          </span>
        )}
        <div className="min-w-0 flex-1">
          <div className="mb-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-foreground-500">
            <span className="rounded-full bg-secondary-100 px-2.5 py-0.5 text-secondary-900">
              {getCategoryName(entry.category)}
            </span>
            <span>{formatDate(entry.updated)} 更新</span>
            <span className="flex items-center gap-1 text-accent-700">
              <i className="ri-fire-line" />
              {formatHeat(entry.heat)}
            </span>
          </div>
          <h3 className="text-sm font-semibold leading-relaxed text-foreground-950 md:text-base">
            <Link to={`/entry/${entry.id}`} className="transition-colors group-hover:text-primary-600">
              {entry.title}
            </Link>
          </h3>
          <p className="mt-1 line-clamp-2 hidden text-sm leading-relaxed text-foreground-600 sm:block">
            {entry.summary}
          </p>
        </div>
        <div className="hidden h-16 w-24 shrink-0 overflow-hidden rounded-md bg-background-100 md:block">
          <img
            src={entry.cover}
            alt={`${entry.title}配图`}
            title={`${entry.persona}话题汇总配图`}
            className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          />
        </div>
      </article>
    );
  }

  return (
    <article className="group flex h-full flex-col overflow-hidden rounded-lg border border-background-200/70 bg-background-50 transition-colors hover:border-primary-300">
      <Link to={`/entry/${entry.id}`} className="block h-40 w-full overflow-hidden bg-background-100 md:h-44">
        <img
          src={entry.cover}
          alt={`${entry.title}配图`}
          title={`${entry.persona}话题汇总配图`}
          className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
      </Link>
      <div className="flex flex-1 flex-col p-4 md:p-5">
        <div className="mb-2 flex flex-wrap items-center gap-2 text-xs text-foreground-500">
          <span className="rounded-full bg-secondary-100 px-2.5 py-0.5 text-secondary-900">
            {getCategoryName(entry.category)}
          </span>
          <span className="flex items-center gap-1 text-accent-700">
            <i className="ri-fire-line" />
            {formatHeat(entry.heat)}
          </span>
        </div>
        <h3 className="text-base font-semibold leading-snug text-foreground-950">
          <Link to={`/entry/${entry.id}`} className="transition-colors group-hover:text-primary-600">
            {entry.title}
          </Link>
        </h3>
        <p className="mt-2 line-clamp-3 flex-1 text-sm leading-relaxed text-foreground-600">{entry.summary}</p>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          {entry.tags.slice(0, 3).map((tag) => (
            <span key={tag} className="rounded-md bg-background-100 px-2 py-0.5 text-xs text-foreground-600">
              #{tag}
            </span>
          ))}
        </div>
        <div className="mt-4 flex items-center justify-between border-t border-background-200/70 pt-3 text-xs text-foreground-500">
          <span>{formatDate(entry.updated)} 更新</span>
          <span className="flex items-center gap-1 text-primary-600 transition-colors group-hover:gap-2">
            查看汇总
            <i className="ri-arrow-right-line" />
          </span>
        </div>
      </div>
    </article>
  );
}